<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxesAPI from 'dashboard/api/inboxes';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
});

const emit = defineEmits(['onSend', 'cancel', 'update:show']);

const { t } = useI18n();

const localShow = computed({
  get() {
    return props.show;
  },
  set(value) {
    emit('update:show', value);
  },
});

const isLoading = ref(false);
const isLoadingProducts = ref(false);
const settings = ref({});
const catalogs = ref([]);
const products = ref([]);
const productSearch = ref('');
const form = ref({
  mode: 'product',
  catalogId: '',
  productRetailerId: '',
  productRetailerIds: [],
  thumbnailProductRetailerId: '',
  sectionTitle: '',
  bodyText: '',
  footerText: '',
});

const selectedCatalog = computed(() => {
  return catalogs.value.find(
    catalog => String(catalog.id) === String(form.value.catalogId)
  );
});

const selectedProduct = computed(() => {
  return products.value.find(
    product =>
      String(product.retailer_id || product.id) ===
      String(form.value.productRetailerId)
  );
});

const selectedThumbnailProduct = computed(() => {
  return products.value.find(
    product =>
      String(product.retailer_id || product.id) ===
      String(form.value.thumbnailProductRetailerId)
  );
});
const selectedMultiProducts = computed(() =>
  products.value.filter(product =>
    form.value.productRetailerIds.includes(
      String(product.retailer_id || product.id)
    )
  )
);
const filteredProducts = computed(() => {
  const query = productSearch.value.trim().toLowerCase();
  if (!query) return products.value;

  return products.value.filter(product =>
    [product.name, product.retailer_id, product.description]
      .filter(Boolean)
      .some(value => value.toString().toLowerCase().includes(query))
  );
});

const canSend = computed(() => {
  if (!form.value.catalogId) return false;

  if (form.value.mode === 'catalog') {
    return !!form.value.thumbnailProductRetailerId;
  }

  if (form.value.mode === 'multi_product') {
    return form.value.productRetailerIds.length > 0;
  }

  return !!form.value.productRetailerId;
});

const commerceSettingsSummary = computed(() => {
  const items = [];

  if (settings.value.is_catalog_visible) {
    items.push(t('WHATSAPP_COMMERCE.SETTINGS.CATALOG_VISIBLE'));
  } else {
    items.push(t('WHATSAPP_COMMERCE.SETTINGS.CATALOG_HIDDEN'));
  }

  if (settings.value.is_cart_enabled) {
    items.push(t('WHATSAPP_COMMERCE.SETTINGS.CART_ENABLED'));
  } else {
    items.push(t('WHATSAPP_COMMERCE.SETTINGS.CART_DISABLED'));
  }

  return items.join(' · ');
});

const resetForm = () => {
  products.value = [];
  productSearch.value = '';
  form.value = {
    mode: 'product',
    catalogId: '',
    productRetailerId: '',
    productRetailerIds: [],
    thumbnailProductRetailerId: '',
    sectionTitle: '',
    bodyText: '',
    footerText: '',
  };
};

const loadProducts = async catalogId => {
  if (!props.inboxId || !catalogId) {
    products.value = [];
    return;
  }

  isLoadingProducts.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppCatalogProducts(
      props.inboxId,
      catalogId
    );
    products.value = response.data?.payload || [];

    if (!products.value.length) {
      form.value.productRetailerId = '';
      form.value.thumbnailProductRetailerId = '';
      return;
    }

    const firstRetailerId =
      products.value[0].retailer_id || String(products.value[0].id);

    if (
      !products.value.some(
        product =>
          String(product.retailer_id || product.id) ===
          String(form.value.productRetailerId)
      )
    ) {
        form.value.productRetailerId = firstRetailerId;
    }

    form.value.productRetailerIds = form.value.productRetailerIds.filter(id =>
      products.value.some(
        product => String(product.retailer_id || product.id) === String(id)
      )
    );

    if (
      !products.value.some(
        product =>
          String(product.retailer_id || product.id) ===
          String(form.value.thumbnailProductRetailerId)
      )
    ) {
      form.value.thumbnailProductRetailerId = firstRetailerId;
    }
  } catch (error) {
    products.value = [];
    useAlert(t('WHATSAPP_COMMERCE.PRODUCTS.LOAD_ERROR'));
  } finally {
    isLoadingProducts.value = false;
  }
};

const loadCommerce = async () => {
  if (!props.inboxId) return;

  isLoading.value = true;
  try {
    const response = await InboxesAPI.getWhatsAppCommerce(props.inboxId);
    settings.value = response.data?.settings || {};
    catalogs.value = response.data?.catalogs || [];

    if (catalogs.value.length) {
      form.value.catalogId = String(catalogs.value[0].id);
      await loadProducts(form.value.catalogId);
    }
  } catch (error) {
    useAlert(t('WHATSAPP_COMMERCE.SETTINGS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const sendCommerceMessage = () => {
  if (!canSend.value) return;

  if (form.value.mode === 'catalog') {
    emit('onSend', {
      message:
        form.value.bodyText.trim() ||
        selectedCatalog.value?.name ||
        t('WHATSAPP_COMMERCE.MESSAGE_FALLBACK.CATALOG'),
      contentAttributes: {
        whatsappCommerce: {
          type: 'catalog',
          catalogId: form.value.catalogId,
          catalogName: selectedCatalog.value?.name,
          thumbnailProductRetailerId: form.value.thumbnailProductRetailerId,
          productName: selectedThumbnailProduct.value?.name,
        },
      },
      templateParams: {
        message_type: 'commerce_catalog',
        thumbnail_product_retailer_id: form.value.thumbnailProductRetailerId,
        body_text: form.value.bodyText,
        footer_text: form.value.footerText,
      },
    });
    return;
  }

  if (form.value.mode === 'multi_product') {
    emit('onSend', {
      message:
        form.value.bodyText.trim() ||
        t('WHATSAPP_COMMERCE.MESSAGE_FALLBACK.MULTI_PRODUCT'),
      contentAttributes: {
        whatsappCommerce: {
          type: 'multi_product',
          catalogId: form.value.catalogId,
          catalogName: selectedCatalog.value?.name,
          productRetailerIds: form.value.productRetailerIds,
          productNames: selectedMultiProducts.value.map(product => product.name),
          sectionTitle: form.value.sectionTitle,
        },
      },
      templateParams: {
        message_type: 'commerce_multi_product',
        catalog_id: form.value.catalogId,
        product_retailer_ids: form.value.productRetailerIds,
        section_title: form.value.sectionTitle,
        body_text: form.value.bodyText,
        footer_text: form.value.footerText,
      },
    });
    return;
  }

  emit('onSend', {
    message:
      form.value.bodyText.trim() ||
      selectedProduct.value?.name ||
      t('WHATSAPP_COMMERCE.MESSAGE_FALLBACK.PRODUCT'),
    contentAttributes: {
      whatsappCommerce: {
        type: 'product',
        catalogId: form.value.catalogId,
        catalogName: selectedCatalog.value?.name,
        productRetailerId: form.value.productRetailerId,
        productName: selectedProduct.value?.name,
      },
    },
    templateParams: {
      message_type: 'commerce_product',
      catalog_id: form.value.catalogId,
      product_retailer_id: form.value.productRetailerId,
      body_text: form.value.bodyText,
      footer_text: form.value.footerText,
    },
  });
};

const toggleProductSelection = retailerId => {
  if (form.value.productRetailerIds.includes(retailerId)) {
    form.value.productRetailerIds = form.value.productRetailerIds.filter(
      value => value !== retailerId
    );
    return;
  }

  form.value.productRetailerIds = [...form.value.productRetailerIds, retailerId];
};

const onClose = () => {
  emit('cancel');
  resetForm();
};

watch(
  () => props.show,
  value => {
    if (value) {
      loadCommerce();
      return;
    }

    settings.value = {};
    catalogs.value = [];
    resetForm();
  }
);

watch(
  () => form.value.catalogId,
  async (catalogId, previousCatalogId) => {
    if (
      !catalogId ||
      catalogId === previousCatalogId ||
      !props.show ||
      isLoading.value
    ) {
      return;
    }
    await loadProducts(catalogId);
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose" size="modal-big">
    <woot-modal-header
      :header-title="$t('WHATSAPP_COMMERCE.MODAL.TITLE')"
      :header-content="$t('WHATSAPP_COMMERCE.MODAL.SUBTITLE')"
    />
    <div class="row modal-content">
      <div class="w-full">
        <div
          class="mb-4 rounded-xl bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
        >
          {{ commerceSettingsSummary }}
        </div>

        <div v-if="isLoading" class="py-8 text-center text-n-slate-11">
          {{ $t('WHATSAPP_COMMERCE.SETTINGS.LOADING') }}
        </div>

        <div v-else-if="!catalogs.length" class="py-8 text-center text-n-slate-11">
          {{ $t('WHATSAPP_COMMERCE.CATALOGS.EMPTY') }}
        </div>

        <div v-else class="grid gap-4">
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="mode-chip"
              :class="{ 'mode-chip-active': form.mode === 'product' }"
              @click="form.mode = 'product'"
            >
              {{ $t('WHATSAPP_COMMERCE.MODES.PRODUCT') }}
            </button>
            <button
              type="button"
              class="mode-chip"
              :class="{ 'mode-chip-active': form.mode === 'catalog' }"
              @click="form.mode = 'catalog'"
            >
              {{ $t('WHATSAPP_COMMERCE.MODES.CATALOG') }}
            </button>
            <button
              type="button"
              class="mode-chip"
              :class="{ 'mode-chip-active': form.mode === 'multi_product' }"
              @click="form.mode = 'multi_product'"
            >
              {{ $t('WHATSAPP_COMMERCE.MODES.MULTI_PRODUCT') }}
            </button>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="field-wrap">
              <span class="field-label">
                {{ $t('WHATSAPP_COMMERCE.CATALOGS.LABEL') }}
              </span>
              <select v-model="form.catalogId" class="field-select">
                <option
                  v-for="catalog in catalogs"
                  :key="catalog.id"
                  :value="String(catalog.id)"
                >
                  {{ catalog.name || catalog.id }}
                </option>
              </select>
            </label>
          </div>

          <woot-input
            v-if="form.mode === 'product' || form.mode === 'multi_product'"
            v-model="productSearch"
            :label="$t('WHATSAPP_COMMERCE.PRODUCTS.SEARCH_LABEL')"
            :placeholder="$t('WHATSAPP_COMMERCE.PRODUCTS.SEARCH_PLACEHOLDER')"
          />

          <div class="grid gap-4 md:grid-cols-2">
            <label v-if="form.mode === 'product'" class="field-wrap">
              <span class="field-label">
                {{ $t('WHATSAPP_COMMERCE.PRODUCTS.LABEL') }}
              </span>
              <select
                v-model="form.productRetailerId"
                class="field-select"
                :disabled="isLoadingProducts || !products.length"
              >
                <option
                  v-for="product in filteredProducts"
                  :key="product.retailer_id || product.id"
                  :value="String(product.retailer_id || product.id)"
                >
                  {{ product.name || product.retailer_id || product.id }}
                </option>
              </select>
            </label>

            <label v-else-if="form.mode === 'catalog'" class="field-wrap">
              <span class="field-label">
                {{ $t('WHATSAPP_COMMERCE.PRODUCTS.THUMBNAIL_LABEL') }}
              </span>
              <select
                v-model="form.thumbnailProductRetailerId"
                class="field-select"
                :disabled="isLoadingProducts || !products.length"
              >
                <option
                  v-for="product in products"
                  :key="product.retailer_id || product.id"
                  :value="String(product.retailer_id || product.id)"
                >
                  {{ product.name || product.retailer_id || product.id }}
                </option>
              </select>
            </label>
          </div>

          <div
            v-if="form.mode === 'multi_product' && filteredProducts.length"
            class="grid gap-3 rounded-xl border border-n-container bg-n-alpha-2 p-3"
          >
            <div class="flex items-center justify-between gap-3">
              <p class="mb-0 text-sm font-medium text-n-slate-12">
                {{ $t('WHATSAPP_COMMERCE.PRODUCTS.MULTI_LABEL') }}
              </p>
              <span class="text-xs text-n-slate-10">
                {{ form.productRetailerIds.length }}
              </span>
            </div>
            <woot-input
              v-model="form.sectionTitle"
              :label="$t('WHATSAPP_COMMERCE.PRODUCTS.SECTION_TITLE')"
              :placeholder="$t('WHATSAPP_COMMERCE.PRODUCTS.SECTION_TITLE_PLACEHOLDER')"
            />
            <div class="grid gap-2 max-h-64 overflow-auto">
              <label
                v-for="product in filteredProducts"
                :key="`multi-${product.retailer_id || product.id}`"
                class="flex items-start gap-3 rounded-lg border border-n-container bg-n-background px-3 py-2 text-sm text-n-slate-12"
              >
                <input
                  type="checkbox"
                  :checked="
                    form.productRetailerIds.includes(
                      String(product.retailer_id || product.id)
                    )
                  "
                  @change="
                    toggleProductSelection(
                      String(product.retailer_id || product.id)
                    )
                  "
                />
                <div class="min-w-0">
                  <p class="mb-0 font-medium">
                    {{ product.name || product.retailer_id || product.id }}
                  </p>
                  <p class="mt-1 mb-0 text-xs text-n-slate-11">
                    {{ product.retailer_id || product.id }}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <p v-if="isLoadingProducts" class="mb-0 text-xs text-n-slate-11">
            {{ $t('WHATSAPP_COMMERCE.PRODUCTS.LOADING') }}
          </p>

          <p
            v-else-if="!products.length"
            class="mb-0 rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
          >
            {{ $t('WHATSAPP_COMMERCE.PRODUCTS.EMPTY') }}
          </p>

          <p
            v-else-if="(form.mode === 'product' || form.mode === 'multi_product') && !filteredProducts.length"
            class="mb-0 rounded-lg bg-n-alpha-2 px-3 py-2 text-xs text-n-slate-11"
          >
            {{ $t('WHATSAPP_COMMERCE.PRODUCTS.FILTER_EMPTY') }}
          </p>

          <woot-input
            v-model="form.bodyText"
            :label="$t('WHATSAPP_COMMERCE.FORM.BODY_LABEL')"
            :placeholder="$t('WHATSAPP_COMMERCE.FORM.BODY_PLACEHOLDER')"
          />

          <woot-input
            v-model="form.footerText"
            :label="$t('WHATSAPP_COMMERCE.FORM.FOOTER_LABEL')"
            :placeholder="$t('WHATSAPP_COMMERCE.FORM.FOOTER_PLACEHOLDER')"
          />

          <div class="flex justify-end gap-2">
            <NextButton slate @click="onClose">
              {{ $t('WHATSAPP_COMMERCE.FORM.CANCEL') }}
            </NextButton>
            <NextButton
              :disabled="!canSend || isLoading || isLoadingProducts"
              @click="sendCommerceMessage"
            >
              {{ $t('WHATSAPP_COMMERCE.FORM.SEND') }}
            </NextButton>
          </div>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style lang="scss" scoped>
.mode-chip {
  @apply rounded-full border border-n-container bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-11 transition-colors;
}

.mode-chip-active {
  @apply border-n-brand bg-n-alpha-2 text-n-brand;
}

.field-wrap {
  @apply flex flex-col gap-1.5;
}

.field-label {
  @apply text-xs font-medium text-n-slate-11;
}

.field-select {
  @apply h-10 rounded-lg border border-n-strong bg-n-background px-3 text-sm text-n-slate-12 outline-none transition-colors;

  &:focus {
    @apply border-n-brand;
  }

  &:disabled {
    @apply cursor-not-allowed opacity-60;
  }
}
</style>
